import { useState, useMemo, useRef } from 'react'
import { Plus, Pencil, Trash2, Upload, Save, X, Search, FileText, Link as LinkIcon, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useSDRList, useSDRFacets, useSDRCreate, useSDRUpdate, useSDRDelete, sdrUploadFile, type SDRDoc } from '@/hooks/useSalesDataRoom'

const NCC_PRESETS = ['IGA', 'GRCWay', 'Data Privacy', 'Data Discovery', 'Canal de Denúncias', 'Roadmap', 'Documentação', 'GRC', 'Outros']
const FW_PRESETS = ['LGPD', 'GDPR', 'ISO 27001', 'ISO 27701', 'SOX', 'CVM 80', 'BCB-498', 'ANPD', 'AI Act', 'Multirregulatório']

interface FormState {
  title: string
  description: string
  ncc_module: string
  regulatory_framework: string
  external_link: string
  file_path: string
  file_name: string
  file_type: string
  file_size: number
  is_active: boolean
}

const empty: FormState = {
  title: '',
  description: '',
  ncc_module: '',
  regulatory_framework: '',
  external_link: '',
  file_path: '',
  file_name: '',
  file_type: '',
  file_size: 0,
  is_active: true,
}

export default function SalesDataRoomAdminPage() {
  const { data: docs } = useSDRList({ only_active: false })
  const { data: facets } = useSDRFacets()
  const createMut = useSDRCreate()
  const updateMut = useSDRUpdate()
  const deleteMut = useSDRDelete()

  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<SDRDoc | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<FormState>(empty)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!docs) return []
    const q = search.trim().toLowerCase()
    if (!q) return docs
    return docs.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        (d.ncc_module || '').toLowerCase().includes(q) ||
        (d.regulatory_framework || '').toLowerCase().includes(q)
    )
  }, [docs, search])

  const openNew = () => {
    setEditing(null)
    setForm(empty)
    setShowForm(true)
    setUploadError(null)
  }

  const openEdit = (doc: SDRDoc) => {
    setEditing(doc)
    setForm({
      title: doc.title,
      description: doc.description,
      ncc_module: doc.ncc_module || '',
      regulatory_framework: doc.regulatory_framework || '',
      external_link: doc.external_link || '',
      file_path: doc.file_path || '',
      file_name: doc.file_name || '',
      file_type: doc.file_type || '',
      file_size: doc.file_size || 0,
      is_active: doc.is_active,
    })
    setShowForm(true)
    setUploadError(null)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditing(null)
    setForm(empty)
    setUploadError(null)
  }

  const handleUpload = async (file: File) => {
    setUploadError(null)
    setUploading(true)
    try {
      const result = await sdrUploadFile(file)
      setForm((f) => ({ ...f, file_path: result.file_path, file_name: result.file_name, file_type: result.file_type, file_size: result.file_size }))
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setUploadError(ax?.response?.data?.detail || 'Falha no upload')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.description.trim()) return
    const payload = {
      ...form,
      ncc_module: form.ncc_module || null,
      regulatory_framework: form.regulatory_framework || null,
      external_link: form.external_link || null,
      file_path: form.file_path || null,
      file_name: form.file_name || null,
      file_type: form.file_type || null,
      file_size: form.file_size || null,
    }
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload })
    } else {
      await createMut.mutateAsync(payload)
    }
    closeForm()
  }

  const handleDelete = async (doc: SDRDoc) => {
    if (!confirm(`Excluir "${doc.title}"? Essa ação não pode ser desfeita.`)) return
    await deleteMut.mutateAsync(doc.id)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Link to="/settings" className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-2">
              <ArrowLeft className="h-3 w-3" /> Configurações
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sales Data Room — Administração</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Cadastre, edite e organize documentos do repositório comercial.</p>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
          >
            <Plus className="h-4 w-4" /> Novo Documento
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar título, descrição, módulo, framework..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
          />
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-4 py-3">Título</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-4 py-3 hidden md:table-cell">Módulo NCC</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-4 py-3 hidden md:table-cell">Framework</th>
                <th className="text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-4 py-3 hidden lg:table-cell">Recurso</th>
                <th className="text-right text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">
                    {search ? 'Nenhum documento corresponde à busca.' : 'Nenhum documento cadastrado.'}
                  </td>
                </tr>
              ) : (
                filtered.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sm text-gray-900 dark:text-white">{doc.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{doc.description}</div>
                      {!doc.is_active && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase">Inativo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {doc.ncc_module && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider">
                          {doc.ncc_module}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {doc.regulatory_framework && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                          {doc.regulatory_framework}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {doc.file_path ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <FileText className="h-3 w-3" /> Arquivo
                        </span>
                      ) : doc.external_link ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                          <LinkIcon className="h-3 w-3" /> Link
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(doc)}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        aria-label="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-1"
                        aria-label="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeForm}>
          <form
            onSubmit={handleSubmit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editing ? 'Editar Documento' : 'Novo Documento'}
              </h2>
              <button type="button" onClick={closeForm} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Fechar">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Title */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Título <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
              </div>

              {/* Description */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none resize-none"
                />
              </div>

              {/* NCC Module */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Módulo NCC
                </label>
                <input
                  type="text"
                  list="ncc-presets"
                  value={form.ncc_module}
                  onChange={(e) => setForm({ ...form, ncc_module: e.target.value })}
                  placeholder="ex: IGA, GRCWay..."
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
                <datalist id="ncc-presets">
                  {[...new Set([...NCC_PRESETS, ...(facets?.ncc_modules || [])])].map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>

              {/* Regulatory Framework */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Framework Regulatório
                </label>
                <input
                  type="text"
                  list="fw-presets"
                  value={form.regulatory_framework}
                  onChange={(e) => setForm({ ...form, regulatory_framework: e.target.value })}
                  placeholder="ex: LGPD, ISO 27001..."
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
                <datalist id="fw-presets">
                  {[...new Set([...FW_PRESETS, ...(facets?.regulatory_frameworks || [])])].map((f) => (
                    <option key={f} value={f} />
                  ))}
                </datalist>
              </div>

              {/* External link */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Link Externo
                </label>
                <input
                  type="url"
                  value={form.external_link}
                  onChange={(e) => setForm({ ...form, external_link: e.target.value })}
                  placeholder="https://social.myncc.com.br/portfolio/..."
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                />
              </div>

              {/* File upload */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
                  Arquivo (PDF, DOC, DOCX, XLSX, PNG · até 30 MB)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xlsx,.png"
                    onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
                    className="hidden"
                    id="sdr-file-input"
                  />
                  <label
                    htmlFor="sdr-file-input"
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                  >
                    <Upload className="h-4 w-4" />
                    {uploading ? 'Enviando...' : form.file_name ? 'Trocar arquivo' : 'Importar arquivo'}
                  </label>
                  {form.file_name && (
                    <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1">
                      <span className="font-mono text-emerald-600 dark:text-emerald-400">{form.file_type}</span> · {form.file_name}
                    </span>
                  )}
                </div>
                {uploadError && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{uploadError}</p>}
              </div>

              {/* Active toggle */}
              <div className="sm:col-span-2">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Ativo (visível na galeria)
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeForm}
                className="px-4 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!form.title || !form.description || createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
              >
                <Save className="h-4 w-4" />
                {editing ? 'Salvar Alterações' : 'Criar Documento'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
